import { useState } from "react";
import { ABSENCE_TYPE_LABELS, type CoverageDTO } from "@easyshop/shared";
import { ErrorState, Loading, Pill, cx } from "@/components/ui";
import { IosGroup, IosRow, IosSection } from "@/components/mobile/MobileUi";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { useMyPlanning, usePlanning } from "@/lib/queries";
import { ShiftDrawer, type ShiftDraft } from "@/components/planning/ShiftDrawer";
import { DayPromoChips, UpcomingPromosGroup, promosOn } from "@/components/planning/PromoChips";
import { addDaysYmd, dateShort, dayLong, hoursFmt, mondayYmd, parseYmd, ymd } from "@/components/planning/utils";

const DOW = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

/** Numéro de semaine ISO 8601 d'un lundi AAAA-MM-JJ. */
function isoWeek(monday: string): number {
  const d = parseYmd(monday);
  const thursday = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 3);
  const jan1 = new Date(thursday.getFullYear(), 0, 1);
  // Arrondi au jour : l'écart peut perdre une heure au passage à l'heure d'été.
  return Math.floor(Math.round((thursday.getTime() - jan1.getTime()) / 86_400_000) / 7) + 1;
}

/** « Semaine 41 · 5 – 11 oct. » (« 28 sept. – 4 oct. » à cheval sur deux mois). */
function weekTitle(from: string): string {
  const to = addDaysYmd(from, 6);
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return `Semaine ${isoWeek(from)} · ${sameMonth ? parseYmd(from).getDate() : dateShort(from)} – ${dateShort(to)}`;
}

/** « Aujourd'hui », « Demain » ou « Jeudi 8 octobre ». */
function dayRelative(day: string, today: string): string {
  if (day === today) return "Aujourd'hui";
  if (day === addDaysYmd(today, 1)) return "Demain";
  const s = dayLong(day);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** État d'un jour pour le point de la bande : couvert, sous-effectif ou rien (fermé / personne). */
function dayState(cov: CoverageDTO | undefined): "ok" | "under" | null {
  if (cov?.understaffed) return "under";
  return cov && cov.staff > 0 ? "ok" : null;
}

/**
 * Planning mobile : mon prochain service, la semaine de la boutique sélectionnée
 * (bande de 7 jours avec couverture), puis le détail du jour choisi (créneaux de
 * l'équipe, absences, promos) et les prochaines promos.
 */
export function MobilePlanning() {
  const { selectedId } = useSelectedBoutique();
  const today = ymd(new Date());
  const [from, setFrom] = useState(mondayYmd());
  const [picked, setPicked] = useState<string | null>(null);
  const q = usePlanning(from, selectedId ? [selectedId] : []);
  const mine = useMyPlanning();
  const [draft, setDraft] = useState<ShiftDraft | null>(null);
  const d = q.data;
  const staffById = new Map((d?.staff ?? []).map((s) => [s.id, s]));

  const week = Array.from({ length: 7 }, (_, i) => addDaysYmd(from, i));
  // Jour affiché : celui touché, sinon aujourd'hui s'il est dans la semaine, sinon le lundi.
  const day = picked && week.includes(picked) ? picked : week.includes(today) ? today : from;
  const goWeek = (n: number) => {
    setFrom(addDaysYmd(from, 7 * n));
    setPicked(null);
  };

  // Mon prochain service (aujourd'hui pas encore terminé, ou plus tard) et mes heures de la semaine affichée.
  const myIds = new Set(mine.data?.staffMemberIds ?? []);
  const nowHm = new Date().toTimeString().slice(0, 5);
  const next = mine.data?.shifts.find((s) => s.date > today || (s.date === today && s.end > nowHm));
  const myWeekHours = (d?.from === from ? d.shifts : []).filter((s) => myIds.has(s.staffMemberId)).reduce((t, s) => t + s.hours, 0);

  const cov = d?.coverage.find((c) => c.date === day);
  const shifts = (d?.shifts ?? []).filter((s) => s.date === day).sort((a, b) => a.start.localeCompare(b.start));
  const absent = (d?.absences ?? []).filter((a) => a.startDate <= day && a.endDate >= day);
  const promos = d ? promosOn(d.promos, day, selectedId ?? undefined) : [];
  const missing = cov?.understaffed ? Math.max(1, cov.minStaff - cov.staff) : 0;

  return (
    <>
      {myIds.size > 0 && (
        <div className="flex items-center gap-[14px] rounded-ios bg-white px-4 py-[14px]">
          <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span className="text-[13px] text-muted">Mon prochain service</span>
            {next ? (
              <>
                <span className="truncate text-[17px] font-semibold tabular">
                  {dayRelative(next.date, today)} · {next.start}–{next.end}
                </span>
                <span className="truncate text-[13px] text-muted">{next.city}</span>
              </>
            ) : (
              <span className="text-[17px] font-semibold">{mine.isLoading ? "…" : "Aucun service prévu"}</span>
            )}
          </div>
          <div className="flex flex-shrink-0 flex-col items-end">
            <span className="whitespace-nowrap text-[22px] font-bold tabular">{hoursFmt(myWeekHours)}</span>
            <span className="text-[12px] text-muted">{from === mondayYmd() ? "cette semaine" : `semaine ${isoWeek(from)}`}</span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-[6px] rounded-ios bg-white px-[6px] pb-[10px] pt-1">
        <div className="flex items-center">
          <button type="button" className="min-h-[44px] min-w-[44px] text-[22px] leading-none text-accent" aria-label="Semaine précédente" onClick={() => goWeek(-1)}>
            ‹
          </button>
          <span className="flex-1 text-center text-[15px] font-semibold tabular">{weekTitle(from)}</span>
          <button type="button" className="min-h-[44px] min-w-[44px] text-[22px] leading-none text-accent" aria-label="Semaine suivante" onClick={() => goWeek(1)}>
            ›
          </button>
        </div>
        <div className="grid grid-cols-7">
          {week.map((w, i) => {
            const on = w === day;
            const isT = w === today;
            const st = dayState(d?.coverage.find((c) => c.date === w));
            return (
              <button
                key={w}
                type="button"
                aria-pressed={on}
                aria-label={dayLong(w)}
                onClick={() => setPicked(w)}
                className="flex min-h-[70px] flex-col items-center gap-1 py-[2px]"
              >
                <span className={cx("text-[12px] font-semibold uppercase", isT ? "text-accent" : "text-muted")}>{DOW[i]}</span>
                <span
                  className={cx(
                    "grid h-[34px] w-[34px] place-items-center rounded-full text-[17px] tabular",
                    on ? "bg-accent font-semibold text-white" : isT ? "font-semibold text-accent ring-[1.5px] ring-inset ring-accent" : "text-ink",
                  )}
                >
                  {parseYmd(w).getDate()}
                </span>
                <span className={cx("h-[6px] w-[6px] rounded-full", st === "ok" ? "bg-positive" : st === "under" ? "bg-danger" : "bg-transparent")} />
              </button>
            );
          })}
        </div>
      </div>

      {q.isLoading && <Loading />}
      {q.error && <ErrorState error={q.error} />}
      {d && (
        <IosSection
          title={dayLong(day)}
          action={
            cov &&
            (cov.staff > 0 || cov.understaffed) && (
              <span className={cx("font-semibold tabular", cov.understaffed ? "text-danger" : "text-positive")}>
                {cov.staff} / {cov.minStaff} pers.
              </span>
            )
          }
        >
          <IosGroup>
            {shifts.length === 0 && (
              <IosRow>
                <span className="text-[16px] text-muted">Personne de planifié ce jour-là.</span>
              </IosRow>
            )}
            {shifts.map((s) => {
              const p = staffById.get(s.staffMemberId);
              return (
                <IosRow
                  key={s.id}
                  className="min-h-[58px]"
                  onClick={d.canManage ? () => setDraft({ shift: s, boutiqueId: s.boutiqueId, date: day }) : undefined}
                  chevron={false}
                  left={<span className="my-[10px] w-[4px] flex-shrink-0 self-stretch rounded-[2px]" style={{ background: p?.color ?? "var(--accent)" }} />}
                  right={<span className="flex-shrink-0 text-[15px] font-semibold tabular">{s.start}–{s.end}</span>}
                >
                  <span className="block truncate text-[16px]">{p?.name ?? "—"}</span>
                  <span className="block text-[13px] text-muted">{s.start < "13:00" ? "Matin" : "Après-midi"}</span>
                </IosRow>
              );
            })}
            {absent.map((a) => (
              <IosRow key={a.id} left={<Pill className="bg-warn-bg text-warn">{ABSENCE_TYPE_LABELS[a.type]}</Pill>}>
                <span className="block truncate text-[16px] text-muted">{staffById.get(a.staffMemberId)?.name}</span>
              </IosRow>
            ))}
            {missing > 0 && (
              <IosRow>
                <span className="text-[15px] text-danger">
                  Il manque {missing} personne{missing > 1 ? "s" : ""}
                </span>
              </IosRow>
            )}
            {promos.length > 0 && (
              <div className="ios-sep px-4 pb-2 pt-1">
                <DayPromoChips promos={promos} compact />
              </div>
            )}
            {d.canManage && selectedId && (
              <IosRow onClick={() => setDraft({ boutiqueId: selectedId, date: day })} chevron={false}>
                <span className="text-[16px] text-accent">+ Ajouter un créneau</span>
              </IosRow>
            )}
          </IosGroup>
        </IosSection>
      )}

      {d && <UpcomingPromosGroup promos={d.upcomingPromos} />}
      {d && <ShiftDrawer draft={draft} staff={d.staff} onClose={() => setDraft(null)} />}
    </>
  );
}
